// Shared between the teams list (organization-page.component) and the badge legend
// (teams-info-panel.component) so both stay visually and textually in sync.
export type TeamStatusKey = "invited" | "awaiting" | "member" | "owner";

export const TEAM_STATUS_LABEL_KEY: Record<TeamStatusKey, string> = {
  invited: "statusInvited",
  awaiting: "statusAwaiting",
  member: "statusMember",
  owner: "statusOwner",
};

export const TEAM_STATUS_DESCRIPTION_KEY: Record<TeamStatusKey, string> = {
  invited: "statusInvitedDesc",
  awaiting: "statusAwaitingDesc",
  member: "statusMemberDesc",
  owner: "statusOwnerDesc",
};

// Fixed-size "Soft" status badge component (GLOSS DS): 80x28, min-width 60, full radius.
// Awaiting/Owner share the same Info/Soft styling per the DS spec — they're distinguished by
// label text, not color.
const BADGE_BASE =
  "tw-inline-flex tw-h-7 tw-w-20 tw-min-w-[60px] tw-shrink-0 tw-items-center tw-justify-center tw-gap-1.5 tw-rounded-full tw-px-2 tw-text-xs tw-font-medium";

// Invitee-side only (Teams > Invitations), so it isn't one of the TeamStatusKey states the
// badge legend explains: an admin rejected the invitee instead of confirming them.
// Same pill as BADGE_BASE, but sized to its label: "Not approved" doesn't fit the fixed 80px
// width and would wrap onto two lines.
export const INVITATION_NOT_APPROVED_BADGE_CLASS =
  "tw-inline-flex tw-h-7 tw-min-w-[80px] tw-shrink-0 tw-items-center tw-justify-center tw-whitespace-nowrap tw-rounded-full tw-px-3 tw-text-xs tw-font-medium tw-border tw-border-solid tw-border-color-border-subtle tw-bg-color-surface-card tw-text-color-text-secondary";

// Owner-side "Declined" beside Invite again in a team's member list: a compact version of the
// Not approved pill, so the row keeps room for the subtitle's date.
export const TEAM_MEMBER_DECLINED_BADGE_CLASS =
  "tw-inline-flex tw-h-6 tw-shrink-0 tw-items-center tw-justify-center tw-whitespace-nowrap tw-rounded-full tw-px-2 tw-text-[11px] tw-font-medium tw-leading-none tw-border tw-border-solid tw-border-color-border-subtle tw-bg-color-surface-card tw-text-color-text-secondary";

export const TEAM_STATUS_BADGE_CLASS: Record<TeamStatusKey, string> = {
  invited: `${BADGE_BASE} tw-bg-color-status-warning-bg tw-text-color-status-warning-text`,
  awaiting: `${BADGE_BASE} tw-bg-color-status-info-bg tw-text-color-status-info-text`,
  member: `${BADGE_BASE} tw-bg-color-surface-sunken tw-text-color-text-secondary`,
  owner: `${BADGE_BASE} tw-bg-color-status-info-bg tw-text-color-status-info-text`,
};
